import React, { useState } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { decodeAndSanitizeHtml } from "./utils/emailAnalysisUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faInfoCircle,
  faEdit,
  faChartBar,
  faPaperPlane,
  faTimes,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import DisclosuresModal from "../common/DisclosuresModal";
import LinkAnalysisSection from "../common/LinkAnalysisSection";
import SuggestionsBulletList from "../common/SuggestionsBulletList";
import "../../css/Common.css";
import "./css/EmailManualScan.css";
import HelpGuideButton from "../../HelpGuideButton";
import ThreatBannerShareButton from "../common/ThreatBannerShareButton";
import EmptyStatePanel from "../common/EmptyStatePanel";

const EmailManualScanDesktop = ({
  emailContent,
  userContext,
  setUserContext,
  analysisResult,
  error,
  isLoading,
  reportId,
  handleInputChange,
  handleSubmit,
  handleReset,
}) => {
  const [explanationCollapsed, setExplanationCollapsed] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <div className="common-header">
        <h1 className="main-title">{t("manual.page.title")}</h1>
        <div className="subtitle-with-help">
          <p className="common-subtitle">{t("manual.page.subtitle")}</p>
          <HelpGuideButton guideId={11} />
        </div>
      </div>

      <div className="common-layout">
        {/* Email Input Form */}
        <div className="analysis-form-container" style={{ flex: 1 }}>
          <div className="common-section-header">
            <h3>
              <FontAwesomeIcon icon={faEdit} />
              {t("manual.form.heading")}
            </h3>
          </div>
          <div className="panel-content">
            {error && (
              <div className="error-message">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="analysis-form">
              <div className="common-form-row">
                <div className="common-form-field full-width-field">
                  <label htmlFor="senderEmail">
                    {t("manual.form.senderLabel")}
                  </label>
                  <input
                    type="text"
                    id="senderEmail"
                    name="senderEmail"
                    value={emailContent.senderEmail}
                    onChange={handleInputChange}
                    placeholder="example@domain.com"
                    required
                    disabled={isLoading}
                    className="compact"
                  />
                </div>
              </div>

              <div className="common-form-row">
                <div className="common-form-field full-width-field">
                  <label htmlFor="subject">
                    {t("manual.form.subjectLabel")}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={emailContent.subject}
                    onChange={handleInputChange}
                    placeholder="Email subject line"
                    required
                    disabled={isLoading}
                    className="compact"
                  />
                </div>
              </div>

              <div className="common-form-row">
                <div className="common-form-field full-width-field">
                  <label htmlFor="body">{t("manual.form.bodyLabel")}</label>
                  <textarea
                    id="body"
                    name="body"
                    value={emailContent.body}
                    onChange={handleInputChange}
                    placeholder={t("manual.form.bodyPlaceholder")}
                    rows={8}
                    required
                    disabled={isLoading}
                    className="compact"
                  />
                </div>
              </div>

              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label htmlFor="email-context-input">
                  <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
                  {t("manual.form.contextLabel")}
                </label>
                <textarea
                  id="email-context-input"
                  value={userContext}
                  onChange={(e) => setUserContext(e.target.value)}
                  placeholder={t("manual.form.contextPlaceholder")}
                  className="additional-context-textarea"
                  rows={3}
                />
              </div>

              <div className="btn-group">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  {isLoading
                    ? t("manual.form.analyzing")
                    : t("manual.form.analyzeBtn")}
                </button>

                {(emailContent.senderEmail ||
                  emailContent.subject ||
                  emailContent.body) && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    {t("manual.form.clearBtn")}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Analysis Results */}
        <div className="analysis-details" style={{ flex: 1 }}>
          <div className="common-section-header">
            <h3>
              <FontAwesomeIcon icon={faChartBar} />
              {t("manual.results.heading")}
            </h3>
          </div>
          <div className="panel-content">
            {analysisResult ? (
              <>
                {/* Threat Assessment Banner */}
                <div
                  className={`threat-banner ${
                    analysisResult.isScam ? "threat-detected" : "safe"
                  }`}
                >
                  <div className="threat-icon">
                    <FontAwesomeIcon
                      icon={
                        analysisResult.isScam
                          ? faExclamationTriangle
                          : faCheckCircle
                      }
                    />
                  </div>
                  <div className="threat-content">
                    <h2 className="threat-title">
                      {analysisResult.isScam
                        ? t("email.detail.scamDetected")
                        : t("email.detail.noThreats")}
                    </h2>
                    <p className="threat-subtitle">
                      {analysisResult.isScam
                        ? t("email.detail.scamSubtitle")
                        : t("manual.results.safeSubtitle")}
                    </p>
                  </div>
                  <ThreatBannerShareButton
                    scamDetected={analysisResult.isScam === true}
                    threatType={t("email.share.threatType")}
                    threatTitle={t("email.share.threatTitle")}
                    threatDetail={t("email.detail.scamSubtitle")}
                    reportId={reportId}
                  />
                </div>

                {/* Scam Type */}
                {analysisResult.scamType &&
                  analysisResult.scamType !== "UNKNOWN" &&
                  analysisResult.scamType !== "None" && (
                    <div
                      className="scam-type-display"
                      style={{ marginTop: "var(--spacing-md, 12px)" }}
                    >
                      <FontAwesomeIcon icon={faExclamationTriangle} />{" "}
                      {t("manual.results.scamType")}{" "}
                      <span className="scam-type-badge">
                        {analysisResult.scamType
                          .replace(/_/g, " ")
                          .toLowerCase()
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                    </div>
                  )}

                {/* What Should You Do? */}
                {analysisResult.suggestions && (
                  <div className="suggestions-section">
                    <h4 className="subsection-title">
                      <FontAwesomeIcon icon={faInfoCircle} />{" "}
                      {t("email.section.whatToDo")}
                    </h4>
                    <SuggestionsBulletList text={analysisResult.suggestions} />
                  </div>
                )}

                {/* Explanation - Collapsible */}
                {analysisResult.explanation && (
                  <div className="explanation-section">
                    <h4
                      className="subsection-title collapsible-header"
                      onClick={() =>
                        setExplanationCollapsed(!explanationCollapsed)
                      }
                      style={{ cursor: "pointer" }}
                    >
                      <FontAwesomeIcon icon={faInfoCircle} />{" "}
                      {t("email.section.explanation")}
                      <FontAwesomeIcon
                        icon={
                          explanationCollapsed ? faChevronDown : faChevronUp
                        }
                        style={{ marginLeft: "auto", fontSize: "0.85rem" }}
                      />
                    </h4>
                    {!explanationCollapsed && (
                      <div
                        className="explanation-text"
                        dangerouslySetInnerHTML={{
                          __html: decodeAndSanitizeHtml(
                            analysisResult.explanation || "",
                          ),
                        }}
                      />
                    )}
                  </div>
                )}

                <LinkAnalysisSection urlAnalyses={analysisResult.urlAnalyses} />
              </>
            ) : (
              <EmptyStatePanel
                title={t("manual.results.noAnalysis")}
                description={t("manual.results.noAnalysisDesc")}
              />
            )}
          </div>
        </div>
      </div>

      <DisclosuresModal disclaimerText={t("email.disclaimer")} />
    </>
  );
};

export default EmailManualScanDesktop;
